import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import FamilyMemberRoster from './FamilyMemberRoster';
import ReflectionCard from '../ReflectionCard';

function FamilyArchiveView({ user }) {
  const [familyData, setFamilyData] = useState(null);
  const [reflections, setReflections] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 20;

  useEffect(() => {
    api.get('/family')
      .then(r => setFamilyData(r.data))
      .catch(() => setError('Could not load family data'))
      .finally(() => setLoading(false));
  }, []);

  const fetchReflections = useCallback(async (memberId, newOffset) => {
    const params = { limit: LIMIT, offset: newOffset };
    if (memberId) params.memberId = memberId;
    const r = await api.get('/family/reflections', { params });
    return r.data;
  }, []);

  useEffect(() => {
    if (!familyData) return;
    setLoading(true);
    setOffset(0);
    fetchReflections(selectedMemberId, 0)
      .then(data => {
        setReflections(data);
        setHasMore(data.length === LIMIT);
      })
      .catch(() => setError('Could not load reflections'))
      .finally(() => setLoading(false));
  }, [selectedMemberId, familyData, fetchReflections]);

  const loadMore = async () => {
    setLoadingMore(true);
    const newOffset = offset + LIMIT;
    try {
      const data = await fetchReflections(selectedMemberId, newOffset);
      setReflections(prev => [...prev, ...data]);
      setOffset(newOffset);
      setHasMore(data.length === LIMIT);
    } catch (_) {
      setError('Could not load more reflections');
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading && !familyData) return <div className="loading-spinner" />;

  if (error) return <div style={{ padding: '2rem', textAlign: 'center', opacity: 0.6 }}>{error}</div>;

  if (!familyData) return (
    <div style={{ padding: '2rem', textAlign: 'center', opacity: 0.6 }}>
      <p>You are not part of a family group yet.</p>
    </div>
  );

  const groupName = familyData.group?.name || 'Family Archive';

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '1.5rem' }}>
      <h2 style={{ marginBottom: '0.25rem' }}>{groupName}</h2>
      <p style={{ opacity: 0.7, fontSize: '0.875em', marginBottom: '1.5rem' }}>
        Shared memories from all family members
      </p>

      <FamilyMemberRoster
        members={familyData.members || []}
        selectedMemberId={selectedMemberId}
        onSelect={setSelectedMemberId}
      />

      {loading && <div className="loading-spinner" />}

      {!loading && reflections.length === 0 && (
        <div style={{ textAlign: 'center', opacity: 0.6, padding: '3rem' }}>
          <p>No shared reflections yet.</p>
          <p style={{ fontSize: '0.875em' }}>Reflections marked "Visible to family" will appear here.</p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {reflections.map(reflection => (
          <div key={reflection.id} style={{ position: 'relative' }}>
            {/* Author label */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', fontSize: '0.8em', opacity: 0.7 }}>
              <div style={{
                width: '20px', height: '20px', borderRadius: '50%',
                background: 'var(--color-primary, #4a90d9)', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 'bold',
              }}>
                {(reflection.username || '?')[0].toUpperCase()}
              </div>
              <span>{reflection.username}</span>
            </div>
            <ReflectionCard
              reflection={reflection}
              user={user}
              onUpdate={() => {}}
              onDelete={() => setReflections(prev => prev.filter(r => r.id !== reflection.id))}
            />
          </div>
        ))}
      </div>

      {hasMore && !loading && (
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <button onClick={loadMore} disabled={loadingMore} className="btn-save">
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

export default FamilyArchiveView;
